const REGISTRANT_SITE_ID_KEYS = [
  'locationSiteId',
  'locationSiteID',
  'location_site_id',
  'siteId',
  'siteID',
  'site_id',
  'siteid',
  'districtId',
  'districtID',
  'district_id',
  'exclusiveSiteId',
  'exclusive_site_id',
  'objId',
  'objid',
  'id',
  'key',
]

const EXCLUSIVE_SITE_ID_KEYS = [
  'location_id',
  'locationId',
  'locationID',
  ...REGISTRANT_SITE_ID_KEYS,
]

const NESTED_SITE_KEYS = ['site', 'locationSite', 'location_site', 'district']

const EXCLUSIVE_GROUP_ID_KEYS = [
  'exclusiveGroupId',
  'exclusiveGroupID',
  'exclusive_group_id',
  'groupId',
  'groupID',
  'group_id',
  'itemId',
  'itemID',
  'item_id',
  'itemPk',
  'itemPK',
  'item_pk',
  'itmId',
  'itmID',
  'itm_id',
  'objId',
  'objID',
  'obj_id',
  'id',
  'key',
]

const USER_GROUP_ID_KEYS = [
  'groupId',
  'groupID',
  'group_id',
  'itemId',
  'itemID',
  'item_id',
  'itemPk',
  'itemPK',
  'item_pk',
  'itmId',
  'itmID',
  'itm_id',
]

const NESTED_GROUP_KEYS = [
  'group',
  'exclusiveGroup',
  'exclusive_group',
  'item',
  'exclusiveItem',
  'exclusive_item',
  'itm',
]

export const EXCLUSIVE_SITE_ELIGIBILITY_MESSAGE =
  'This session is only available to users from its exclusive location site.'

export const EXCLUSIVE_GROUP_ELIGIBILITY_MESSAGE =
  'This session is only available to users from districts in its exclusive group.'

type SiteCarrier = object | null | undefined

function toPositiveNumber(value: unknown): number {
  const parsed = Number(value || 0)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

function readSiteId(source: SiteCarrier, keys: string[]): number {
  if (!source || typeof source !== 'object') return 0

  const values = source as Record<string, unknown>
  for (const key of keys) {
    const value = values[key]
    const id = toPositiveNumber(value)
    if (id) return id
  }

  return 0
}

function readExclusiveSiteId(value: unknown): number {
  const directValue = toPositiveNumber(value)
  if (directValue) return directValue

  if (!value || typeof value !== 'object') return 0

  const source = value as Record<string, unknown>
  const siteId = readSiteId(source, EXCLUSIVE_SITE_ID_KEYS)
  if (siteId) return siteId

  for (const key of NESTED_SITE_KEYS) {
    const nestedId = readExclusiveSiteId(source[key])
    if (nestedId) return nestedId
  }

  return 0
}

function readGroupId(value: unknown, keys: string[]): number {
  const directValue = toPositiveNumber(value)
  if (directValue) return directValue

  if (!value || typeof value !== 'object') return 0

  const source = value as Record<string, unknown>
  const groupId = readSiteId(source, keys)
  if (groupId) return groupId

  for (const key of NESTED_GROUP_KEYS) {
    const nestedId = readGroupId(source[key], keys)
    if (nestedId) return nestedId
  }

  return 0
}

// A restriction row the browser cannot evaluate (see readExclusiveRowSiteIds).
const UNEVALUABLE_EXCLUSIVE_ROW = Symbol('unevaluable exclusive row')

/**
 * Site ids that satisfy one exclusive row. Rows written by the session read model carry a
 * `location_type` and mirror legacy escWorks' Exclusivity tab, where the server is authoritative:
 *  - a row without a location (location_id <= 0) restricts nothing and is ignored, exactly like the
 *    registration check, so open sessions never show a false restriction;
 *  - a district row grants its district (location_id, or the row's location_site_id);
 *  - a campus row is narrowed to the campus by the server at registration, so here it grants the
 *    campus' district. A campus row with no district cannot be evaluated in the browser.
 * Any other shape keeps the generic id lookup.
 */
function readExclusiveRowSiteIds(row: unknown): number[] | typeof UNEVALUABLE_EXCLUSIVE_ROW {
  if (row && typeof row === 'object') {
    const source = row as Record<string, unknown>
    if (typeof source.location_type === 'string') {
      const locationId = toPositiveNumber(source.location_id)
      if (!locationId) return []

      const districtId = toPositiveNumber(source.location_site_id)
      if (source.location_type.toLowerCase() === 'district') {
        return [locationId, districtId].filter(Boolean)
      }
      return districtId ? [districtId] : UNEVALUABLE_EXCLUSIVE_ROW
    }
  }

  return [readExclusiveSiteId(row)].filter(Boolean)
}

export function getSessionExclusiveSiteIds(sessionDetails: unknown): number[] {
  if (!sessionDetails || typeof sessionDetails !== 'object') return []

  const source = sessionDetails as Record<string, unknown>
  const exclusiveSites = source.exclusive_sites ?? source.exclusiveSites
  if (!Array.isArray(exclusiveSites)) return []

  const siteIds = new Set<number>()
  for (const row of exclusiveSites) {
    const rowSiteIds = readExclusiveRowSiteIds(row)
    // Cannot tell which district a row names: do not guess a restriction in the browser; the
    // server still enforces exclusivity when the participant registers.
    if (rowSiteIds === UNEVALUABLE_EXCLUSIVE_ROW) return []
    rowSiteIds.forEach(id => siteIds.add(id))
  }
  return [...siteIds]
}

export function getRegistrantSiteId(registrant: SiteCarrier): number {
  return readSiteId(registrant, REGISTRANT_SITE_ID_KEYS)
}

export function getSessionExclusiveItems(sessionDetails: unknown): unknown[] {
  if (!sessionDetails || typeof sessionDetails !== 'object') return []

  const source = sessionDetails as Record<string, unknown>
  const exclusiveItems = source.exclusive_items ?? source.exclusiveItems

  return Array.isArray(exclusiveItems) ? exclusiveItems : []
}

export function hasSessionExclusiveItems(sessionDetails: unknown): boolean {
  return getSessionExclusiveItems(sessionDetails).length > 0
}

export function getSessionExclusiveGroupIds(sessionDetails: unknown): number[] {
  const exclusiveItems = getSessionExclusiveItems(sessionDetails)

  return [...new Set(exclusiveItems.map(item => readGroupId(item, EXCLUSIVE_GROUP_ID_KEYS)).filter(Boolean))]
}

export function getRegistrantGroupIds(registrantGroups: unknown): number[] {
  if (!Array.isArray(registrantGroups)) return []

  return [...new Set(registrantGroups.map(group => readGroupId(group, USER_GROUP_ID_KEYS)).filter(Boolean))]
}

export function getExclusiveSiteEligibilityErrorForSiteIds(
  exclusiveSiteIds: number[],
  registrantSiteId: unknown,
  message = EXCLUSIVE_SITE_ELIGIBILITY_MESSAGE,
): string | null {
  if (exclusiveSiteIds.length === 0) return null

  const siteId = toPositiveNumber(registrantSiteId)
  return siteId && exclusiveSiteIds.includes(siteId) ? null : message
}

export function getExclusiveSiteEligibilityError(
  sessionDetails: unknown,
  registrantSiteId: unknown,
  message = EXCLUSIVE_SITE_ELIGIBILITY_MESSAGE,
): string | null {
  return getExclusiveSiteEligibilityErrorForSiteIds(
    getSessionExclusiveSiteIds(sessionDetails),
    registrantSiteId,
    message,
  )
}

export function getExclusiveGroupEligibilityErrorForGroupIds(
  exclusiveGroupIds: number[],
  registrantGroupIds: unknown,
  message = EXCLUSIVE_GROUP_ELIGIBILITY_MESSAGE,
): string | null {
  if (exclusiveGroupIds.length === 0) return null

  const groupIds = Array.isArray(registrantGroupIds)
    ? registrantGroupIds.map(group => readGroupId(group, USER_GROUP_ID_KEYS)).filter(Boolean)
    : [toPositiveNumber(registrantGroupIds)].filter(Boolean)

  return groupIds.some(groupId => exclusiveGroupIds.includes(groupId)) ? null : message
}

export function getExclusiveGroupEligibilityError(
  sessionDetails: unknown,
  registrantGroupIds: unknown,
  message = EXCLUSIVE_GROUP_ELIGIBILITY_MESSAGE,
): string | null {
  return getExclusiveGroupEligibilityErrorForGroupIds(
    getSessionExclusiveGroupIds(sessionDetails),
    registrantGroupIds,
    message,
  )
}
